import type { StatusTransMap } from "components/table";
import { OpportunityStatus, QuoteStatus, type OpportunityCounts } from "types/opportunity";

export const OPPORTUNITY_PATH = "/opportunity";
export const QUOTE_PATH = "/quote";

export const opportunityStatusLabel: Record<OpportunityStatus, string> = {
  [OpportunityStatus.GetRequest]: "Lấy yêu cầu",
  [OpportunityStatus.MakePlan]: "Lên phương án",
  [OpportunityStatus.MakeQuote]: "Làm báo giá",
  [OpportunityStatus.WaitApprove]: "Chờ duyệt báo giá",
  [OpportunityStatus.Failed]: "Thất bại",
};

export const quoteStatusLabel: Record<QuoteStatus, string> = {
  [QuoteStatus.WaitSend]: "Chờ gửi khách",
  [QuoteStatus.Sent]: "Đã gửi khách",
  [QuoteStatus.Negotiate]: "Thương lượng",
  [QuoteStatus.Revise]: "Chỉnh sửa báo giá",
  [QuoteStatus.WaitApprove]: "Chờ duyệt báo giá",
  [QuoteStatus.CloseOrder]: "Chốt đơn",
  [QuoteStatus.Failed]: "Thất bại",
};

export const opportunityStatusTrans: StatusTransMap<OpportunityStatus> = {
  [OpportunityStatus.GetRequest]: { label: "Lấy yêu cầu", color: "#b42318", bgcolor: "#fee4e2" },
  [OpportunityStatus.MakePlan]: { label: "Lên phương án", color: "#b54708", bgcolor: "#fdead7" },
  [OpportunityStatus.MakeQuote]: { label: "Làm báo giá", color: "#a15c07", bgcolor: "#fef7c3" },
  [OpportunityStatus.WaitApprove]: { label: "Chờ duyệt báo giá", color: "#175cd3", bgcolor: "#d1e9ff" },
  [OpportunityStatus.Failed]: { label: "Thất bại", color: "#b54708", bgcolor: "#fdead7" },
};

export const quoteStatusTrans: StatusTransMap<QuoteStatus> = {
  [QuoteStatus.WaitSend]: { label: "Chờ gửi khách", color: "#b54708", bgcolor: "#fdead7" },
  [QuoteStatus.Sent]: { label: "Đã gửi khách", color: "#b42318", bgcolor: "#fee4e2" },
  [QuoteStatus.Negotiate]: { label: "Thương lượng", color: "#a15c07", bgcolor: "#fef7c3" },
  [QuoteStatus.Revise]: { label: "Chỉnh sửa báo giá", color: "#b54708", bgcolor: "#fffaeb" },
  [QuoteStatus.WaitApprove]: { label: "Chờ duyệt báo giá", color: "#175cd3", bgcolor: "#d1e9ff" },
  [QuoteStatus.CloseOrder]: { label: "Chốt đơn", color: "#027a48", bgcolor: "#d1fadf" },
  [QuoteStatus.Failed]: { label: "Thất bại", color: "#b54708", bgcolor: "#fdead7" },
};

export const badgeColor = {
  red: "#f04438",
  orange: "#f79009",
  gold: "#eaaa08",
  green: "#12b76a",
  blue: "#2e90fa",
} as const;

export type BadgeTone = keyof typeof badgeColor;

export const opportunityTabs: { key: string; label: string; tone: BadgeTone }[] = [
  { key: "ALL", label: "Tất cả", tone: "red" },
  { key: OpportunityStatus.GetRequest, label: "Lấy yêu cầu", tone: "red" },
  { key: OpportunityStatus.MakePlan, label: "Lên phương án", tone: "orange" },
  { key: OpportunityStatus.MakeQuote, label: "Làm báo giá", tone: "gold" },
  { key: OpportunityStatus.WaitApprove, label: "Chờ duyệt báo giá", tone: "blue" },
  { key: OpportunityStatus.Failed, label: "Thất bại", tone: "orange" },
];

export const quoteTabs: { key: string; label: string; tone: BadgeTone }[] = [
  { key: "ALL", label: "Tất cả", tone: "red" },
  { key: QuoteStatus.WaitSend, label: "Chờ gửi khách", tone: "orange" },
  { key: QuoteStatus.Sent, label: "Đã gửi khách", tone: "red" },
  { key: QuoteStatus.Negotiate, label: "Thương lượng", tone: "gold" },
  { key: QuoteStatus.Revise, label: "Chỉnh sửa báo giá", tone: "orange" },
  { key: QuoteStatus.WaitApprove, label: "Chờ duyệt báo giá", tone: "blue" },
  { key: QuoteStatus.Failed, label: "Thất bại", tone: "orange" },
];

export const quoteStatusOptions = (Object.keys(quoteStatusLabel) as QuoteStatus[]).map((value) => ({
  value,
  label: quoteStatusLabel[value],
  disabled: value === QuoteStatus.CloseOrder,
}));

export const opportunityStatusOptions = (Object.keys(opportunityStatusLabel) as OpportunityStatus[]).map(
  (value) => ({
    value,
    label: opportunityStatusLabel[value],
  }),
);

export const emptyCounts = (): OpportunityCounts => ({
  all: 0,
  noQuote: 0,
  statuses: {
    [OpportunityStatus.GetRequest]: 0,
    [OpportunityStatus.MakePlan]: 0,
    [OpportunityStatus.MakeQuote]: 0,
    [OpportunityStatus.WaitApprove]: 0,
    [OpportunityStatus.Failed]: 0,
  },
  quoteStatuses: {
    [QuoteStatus.WaitSend]: 0,
    [QuoteStatus.Sent]: 0,
    [QuoteStatus.Negotiate]: 0,
    [QuoteStatus.Revise]: 0,
    [QuoteStatus.WaitApprove]: 0,
    [QuoteStatus.CloseOrder]: 0,
    [QuoteStatus.Failed]: 0,
  },
});

export const formatMoney = (value: number) =>
  new Intl.NumberFormat("vi-VN", { maximumFractionDigits: 2 }).format(value || 0);

export const formatQuantity = (value: number) =>
  new Intl.NumberFormat("vi-VN", { maximumFractionDigits: 2 }).format(value || 0);
